import {
  Box,
  Flex,
  Grid,
  Stack,
  Text,
  VStack,
  useMediaQuery,
} from "@chakra-ui/react"
import type { SystemStyleObject } from "@chakra-ui/react"
import {
  FaBookOpen,
  FaCrown,
  FaForward,
  FaHandPointer,
  FaHeart,
  FaLock,
  FaRegHeart,
  FaRocket,
  FaShieldAlt,
  FaStar,
  FaUsers,
} from "react-icons/fa"
import { PiScribbleLoop, PiStarFour, PiStarFourFill } from "react-icons/pi"
import type { IconType } from "react-icons"
import { useAppDispatch } from "../app/hooks"
import { resetGame, setGame, updateGameSlide } from "../features/game/gameSlice"
import { setVariant } from "../features/config/configSlice"
import { getRandomValidTriggerOrSlide } from "../app/gameLogic"
import defaultGameData from "../features/default"
import {
  Burst,
  DISPLAY_FONT,
  Deco,
  IconCircle,
  IllustratedCard,
  IllustratedScreen,
  LANDSCAPE_MQ,
  PillButton,
  SectionLabel,
  TopicTile,
} from "../components/IllustratedScreen"
import { SIDEWAYS_MQ, SIDEWAYS_SMALL_MQ } from "../components/mediaQueries"

// Icons follow the subjects drawn on the client's mockup; colours are
// sampled from it. Each step's number badge takes the step's colour; the
// orange one is deepened just enough for the white digit to reach 3:1
// (#ef8c11 on the mockup). The highlighted word in each caption uses the
// nearest colour that reaches 4.5:1 on white (#ee3070 / #ef8c11 on the
// mockup). The caption wording is unchanged: the word is a styled span.
const steps = [
  {
    before: "Choose what ",
    word: "you",
    after: " think is best",
    icon: FaHandPointer,
    bg: "#f53373",
    badge: "#ee3070",
    wordColor: "#c9245c",
  },
  {
    before: "See what happens ",
    word: "next",
    after: "",
    icon: FaForward,
    bg: "#7e35cc",
    badge: "#7836b8",
    wordColor: "#7836b8",
  },
  {
    before: "Learn ",
    word: "together",
    after: "",
    icon: FaUsers,
    bg: "#f98f06",
    badge: "#d97f0f",
    wordColor: "#a85c08",
  },
]

// The topic names and order are the mockup's (they differ from the welcome
// screen's), each tile in its own pastel.
const topics: {
  label: string
  icon: IconType
  bg: string
  tileBg: string
  tileBorder: string
}[] = [
  {
    label: "Relationships",
    icon: FaHeart,
    bg: "#e62d6c",
    tileBg: "#fccbcb",
    tileBorder: "#eec0bd",
  },
  {
    label: "Health & Protection",
    icon: FaShieldAlt,
    bg: "#76a03f",
    tileBg: "#eaecc6",
    tileBorder: "#dcd6b9",
  },
  {
    label: "Sex & Pressure",
    icon: FaLock,
    bg: "#7b3fa8",
    tileBg: "#e9c8dd",
    tileBorder: "#d6b4c6",
  },
  {
    label: "Education",
    icon: FaBookOpen,
    bg: "#f5a53a",
    tileBg: "#fad78c",
    tileBorder: "#ecc78c",
  },
]

// The corner marks are drawn once per orientation (see decor and the HOW TO
// PLAY row), so each copy shows in one layout only.
const LANDSCAPE_ONLY: SystemStyleObject = {
  display: "none",
  [LANDSCAPE_MQ]: { display: "block" },
}
const PORTRAIT_ONLY: SystemStyleObject = {
  [LANDSCAPE_MQ]: { display: "none" },
}
// The two game buttons on a phone held sideways: their own width, so the
// subtitle keeps to one line beside the label.
const SIDEWAYS_GAME_BUTTON: SystemStyleObject = {
  width: "auto",
  flex: "0 0 auto",
  height: "48px",
  fontSize: "17px",
  marginBottom: "4px",
}

const StepTile = ({
  number,
  before,
  word,
  after,
  icon,
  bg,
  badge,
  wordColor,
  phone,
}: (typeof steps)[number] & { number: number; phone: boolean }) => (
  <Flex
    direction="column"
    align="center"
    textAlign="center"
    position="relative"
    gap={{ base: "6px", md: "8px" }}
    bg="white"
    border="2px solid #f0ddd2"
    borderRadius="20px"
    boxShadow="0 5px 0 #e6c5b5"
    pt={{ base: "20px", md: "24px" }}
    px={{ base: "4px", md: "8px" }}
    pb={{ base: "8px", md: "10px" }}
    mb="5px"
    minH={{ base: "0", md: "150px" }}
    minW={0}
    sx={{
      [SIDEWAYS_MQ]: {
        paddingTop: "15px",
        paddingInline: "4px",
        paddingBottom: "6px",
        gap: "4px",
        minHeight: 0,
        boxShadow: "0 3px 0 #e6c5b5",
        marginBottom: "3px",
      },
    }}
  >
    {/* The number badge straddles the tile's top edge. */}
    <Flex
      position="absolute"
      top={{ base: "-14px", md: "-17px" }}
      left="50%"
      transform="translateX(-50%)"
      align="center"
      justify="center"
      w={{ base: "28px", md: "34px" }}
      h={{ base: "28px", md: "34px" }}
      borderRadius="full"
      bg={badge}
      border="2px solid white"
      color="white"
      fontFamily={DISPLAY_FONT}
      fontWeight={700}
      fontSize={{ base: "16px", md: "20px" }}
      lineHeight="1"
      sx={{
        [SIDEWAYS_MQ]: {
          width: "24px",
          height: "24px",
          top: "-12px",
          fontSize: "14px",
        },
      }}
    >
      {number}
    </Flex>
    <Box position="relative">
      <IconCircle
        icon={icon}
        bg={bg}
        size={phone ? "52px" : "74px"}
        iconSize={phone ? "30px" : "44px"}
        sidewaysSize="44px"
        sidewaysIconSize="26px"
      />
      <Deco
        icon={PiStarFourFill}
        size="12px"
        color={bg}
        top="-4px"
        right="-14px"
      />
      <Deco
        icon={PiStarFourFill}
        size="8px"
        color={bg}
        bottom="4px"
        left="-12px"
      />
    </Box>
    <Text
      fontSize={{ base: "sm", md: "md" }}
      fontWeight="medium"
      color="#4b413c"
      lineHeight="1.15"
      sx={{ [SIDEWAYS_MQ]: { fontSize: "14px" } }}
    >
      {before}
      <Text as="span" color={wordColor} fontWeight="bold">
        {word}
      </Text>
      {after}
    </Text>
  </Flex>
)

const StartScreen: React.FC = () => {
  const dispatch = useAppDispatch()
  // Phones (below Chakra md) get smaller icon circles and stickers; those
  // take px strings, so the width is read once here rather than per tile.
  const [isPhone] = useMediaQuery("(max-width: 47.99em)", {
    ssr: false,
    fallback: false,
  })

  const handleStartPlaying = (variant: "regular" | "srh") => {
    dispatch(setVariant(variant))
    dispatch(resetGame())

    // For SRH, seed the game state with the "srh" tag so deck filtering works immediately
    if (variant === "srh") {
      dispatch(setGame({ tags: ["srh"] }))
    }

    // Small delay to ensure state is flushed, then select the initial slide
    setTimeout(() => {
      const gameData = {
        ...defaultGameData.start,
        tags: variant === "srh" ? ["srh"] : [],
      }
      const initialSlide = getRandomValidTriggerOrSlide(
        defaultGameData.content,
        gameData,
      )

      if (initialSlide) {
        dispatch(updateGameSlide(initialSlide))
      } else {
        // Fallback to default start slide
        dispatch(updateGameSlide(defaultGameData.start.slide!))
      }
    }, 0)
  }

  return (
    <IllustratedScreen>
      <IllustratedCard
        logoSrc="/screens/logo-my-future-my-choice.webp"
        logoAlt="My Future, My Choice"
        logoWidth={1000}
        logoHeight={424}
        // Inside the card's top: three quarters of the card in portrait, as
        // tall as the landscape budget allows (a stand-in logo, see CLAUDE.md),
        // small on a phone held sideways.
        logoSize={{
          portrait: "75%",
          landscape: "min(24vh, 190px)",
          sideways: "40px",
        }}
        logoPlacement="inside"
        maxW={{ portrait: "620px", landscape: "1180px", sideways: "1180px" }}
        logoAsHeading
        logoDecor={
          <>
            {/* Three strokes just beyond each ribbon end, as on the mockup. */}
            <Burst
              color="#b86fa8"
              size="26px"
              right="96%"
              bottom="12%"
              transform="scaleX(-1)"
            />
            <Burst color="#b86fa8" size="26px" left="96%" bottom="12%" />
          </>
        }
        decor={
          // In landscape the card's corner marks sit beside the logo; in
          // portrait they are drawn at the HOW TO PLAY row instead (below).
          <>
            <Deco
              icon={PiStarFour}
              size="24px"
              color="#a98a98"
              left="20px"
              top="64px"
              sx={LANDSCAPE_ONLY}
            />
            <Deco
              icon={FaRegHeart}
              size="20px"
              color="#f0a3b8"
              right="28px"
              top="36px"
              sx={LANDSCAPE_ONLY}
            />
            <Deco
              icon={PiStarFour}
              size="18px"
              color="#f0a3b8"
              right="48px"
              top="58px"
              sx={LANDSCAPE_ONLY}
            />
            <Deco
              icon={PiStarFour}
              size="12px"
              color="#f0a3b8"
              right="26px"
              top="66px"
              sx={LANDSCAPE_ONLY}
            />
          </>
        }
      >
        {/* One column in portrait, as on the mockup. In landscape: how to
            play and the banner on the left, the topics on the right, and the
            game choice across the bottom, so 1280x800 needs no scrolling.
            A phone held sideways: steps left, topics with the banner under
            them right (one column again on the 640-wide phone); the game
            choice is outside the grid (below) so it can stick to the
            bottom of the screen while the page scrolls. */}
        <Box
          display="grid"
          // minmax(0, …): a plain 1fr track cannot shrink below its content's
          // min-content, and the decorations that stick out of the pills and
          // the banner would widen the card past a phone screen.
          gridTemplateColumns="minmax(0, 1fr)"
          gap={{ base: "10px", md: "16px" }}
          sx={{
            [LANDSCAPE_MQ]: {
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
              columnGap: "1.5rem",
              // Without the character block the two columns are close in
              // height; top-aligned, the explore panel sits level with the
              // step tiles.
              alignItems: "start",
            },
            [SIDEWAYS_MQ]: {
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.3fr)",
              gridTemplateRows: "auto auto",
              columnGap: "14px",
              rowGap: "8px",
              alignItems: "start",
            },
            [SIDEWAYS_SMALL_MQ]: {
              gridTemplateColumns: "minmax(0, 1fr)",
              gridTemplateRows: "none",
            },
          }}
        >
          {/* The three grid items are placed by CSS order: in the one-column
              layouts, steps (1), banner (2), explore (3), as before; in
              landscape the banner moves below both columns and spans them
              (steps 1, explore 2, banner 3). Sideways they have grid areas. */}
          <VStack
            spacing="14px"
            align="stretch"
            position="relative"
            minW={0}
            order={1}
            sx={{
              [SIDEWAYS_MQ]: { gridArea: "1 / 1", gap: "6px" },
              [SIDEWAYS_SMALL_MQ]: { gridArea: "auto" },
            }}
          >
            {/* The mockup's marks at this row: an outline star at the left,
                a heart and two stars at the right (under 44px tall, so they
                clear the third tile). Portrait only; see decor above. */}
            <Deco
              icon={PiStarFour}
              size="24px"
              color="#a98a98"
              left="0"
              top="-4px"
              sx={PORTRAIT_ONLY}
            />
            <Deco
              icon={FaRegHeart}
              size="20px"
              color="#f0a3b8"
              right="0"
              top="-12px"
              sx={PORTRAIT_ONLY}
            />
            <Deco
              icon={PiStarFour}
              size="18px"
              color="#f0a3b8"
              right="22px"
              top="8px"
              sx={PORTRAIT_ONLY}
            />
            <Deco
              icon={PiStarFour}
              size="12px"
              color="#f0a3b8"
              right="2px"
              top="18px"
              sx={PORTRAIT_ONLY}
            />
            <SectionLabel>How to play</SectionLabel>
            <Grid
              templateColumns="repeat(3, minmax(0, 1fr))"
              gap={{ base: "8px", md: "17px" }}
              pt="8px"
              sx={{ [SIDEWAYS_MQ]: { gap: "8px", paddingTop: "6px" } }}
            >
              {steps.map((step, index) => (
                <StepTile
                  key={step.word}
                  number={index + 1}
                  phone={isPhone}
                  {...step}
                />
              ))}
            </Grid>
          </VStack>

          {/* The explore section sits on its own pink-cream panel. */}
          <VStack
            order={3}
            sx={{
              [LANDSCAPE_MQ]: { order: 2 },
              [SIDEWAYS_MQ]: {
                gridArea: "1 / 2",
                padding: "6px 10px 8px",
                gap: "6px",
              },
              [SIDEWAYS_SMALL_MQ]: { gridArea: "auto" },
            }}
            spacing="8px"
            align="stretch"
            bg="#fbe6d8"
            borderRadius="22px"
            p={{ base: "8px 11px 10px", md: "12px 14px 14px" }}
          >
            <SectionLabel
              scheme="purple"
              fontSize="16px"
              px="20px"
              sidewaysFontSize="14px"
              sidewaysPx="14px"
            >
              In this game, you&apos;ll explore:
            </SectionLabel>
            <Grid
              templateColumns={{
                base: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              }}
              gap={{ base: "8px", md: "12px" }}
              sx={{ [SIDEWAYS_MQ]: { gap: "6px" } }}
            >
              {topics.map(topic => (
                <TopicTile
                  key={topic.label}
                  layout="column"
                  circleSize={isPhone ? "49px" : "56px"}
                  iconSize={isPhone ? "30px" : "34px"}
                  labelSize="md"
                  {...topic}
                  sparkles={
                    <>
                      <Deco
                        icon={
                          topic.icon === FaHeart ? FaRegHeart : PiStarFourFill
                        }
                        size="10px"
                        color={topic.bg}
                        top="-2px"
                        left="-13px"
                      />
                      <Deco
                        icon={
                          topic.icon === FaHeart ? FaRegHeart : PiStarFourFill
                        }
                        size="7px"
                        color={topic.bg}
                        bottom="2px"
                        right="-12px"
                      />
                    </>
                  }
                />
              ))}
            </Grid>
          </VStack>

          {/* Motto banner: the mockup's yellow band with a heart and a
            crown, "YOUR STORY." in red and underlined. A grid item of its
            own: in portrait it follows the explore panel; in landscape it
            spans both columns, above "Choose your game". */}
          <Box
            position="relative"
            display="flex"
            order={2}
            // The one-column layouts keep the banner 14px under the tiles, as
            // when it sat inside their stack: the grid gap is 10px on phones
            // and 16px on tablets, so top it up / pull it in to 14.
            mt={{ base: "4px", md: "-2px" }}
            sx={{
              [LANDSCAPE_MQ]: { gridColumn: "1 / -1", order: 3, mt: 0 },
              [SIDEWAYS_MQ]: {
                gridArea: "2 / 2",
                marginTop: 0,
                minHeight: "32px",
                paddingBlock: "2px",
                paddingInline: "30px",
              },
              [SIDEWAYS_SMALL_MQ]: { gridArea: "auto" },
            }}
            alignItems="center"
            justifyContent="center"
            minH={{ base: "40px", md: "48px" }}
            px={{ base: 9, md: 10 }}
            py={{ base: 1, md: 0 }}
            bgGradient="linear(to-b, #fcd983, #f2c47a)"
            borderRadius="8px"
          >
            <Deco
              icon={FaHeart}
              size={isPhone ? "40px" : "58px"}
              sidewaysSize="34px"
              color="#f22a6e"
              outline="white"
              left={{ base: "-6px", md: "-12px" }}
              top={{ base: "-8px", md: "-14px" }}
              transform="rotate(-10deg)"
            />
            <Deco
              icon={FaCrown}
              size={isPhone ? "36px" : "52px"}
              sidewaysSize="30px"
              color="#f2bd4a"
              outline="white"
              right={{ base: "-4px", md: "-10px" }}
              top={{ base: "-10px", md: "-16px" }}
              transform="rotate(8deg)"
            />
            <Text
              fontFamily={DISPLAY_FONT}
              fontWeight={700}
              fontSize={{ base: "clamp(15px, 4.6vw, 22px)", md: "26px" }}
              color="#3b2410"
              letterSpacing="0.02em"
              lineHeight="1.15"
              textAlign="center"
              whiteSpace={{ base: "normal", md: "nowrap" }}
              sx={{ [SIDEWAYS_MQ]: { fontSize: "16px" } }}
            >
              YOUR CHOICES SHAPE{" "}
              {/* The mockup's #d1473b falls under 3:1 on the band's lower
                  half; #c23a2d is the nearest red that holds 3:1 there. */}
              <Text
                as="span"
                color="#c23a2d"
                textDecoration="underline"
                textDecorationThickness="2px"
                textUnderlineOffset="3px"
              >
                YOUR STORY.
              </Text>
            </Text>
          </Box>
        </Box>

        {/* The game choice, last in every layout: under the grid, full
            width, the grid's gap above it. Sideways it is one row (the
            label and the two buttons) that sticks to the bottom of the
            screen when the page scrolls, which a grid item could not. */}
        <VStack
          spacing="8px"
          align="stretch"
          position="relative"
          mt={{ base: "10px", md: "16px" }}
          sx={{
            [SIDEWAYS_MQ]: {
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px 14px",
              marginTop: "8px",
              position: "sticky",
              bottom: "8px",
            },
          }}
        >
          <Deco
            icon={PiScribbleLoop}
            size="44px"
            color="#e9a6b4"
            left="9%"
            top="-6px"
            transform="rotate(-10deg)"
          />
          <SectionLabel fontSize="22px" px="24px">
            Choose your game
          </SectionLabel>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={{ base: "14px", md: "17px" }}
            justify="center"
            align="center"
            pt="6px"
            sx={{
              [SIDEWAYS_MQ]: {
                flexDirection: "row",
                gap: "12px",
                paddingTop: 0,
              },
            }}
          >
            {/* Sideways the buttons keep their own width (48px tall, 17px),
                so the subtitle stays on one line. */}
            <PillButton
              scheme="pink"
              subtitle="Start your story adventure"
              uppercase
              sticker={
                <Deco
                  icon={FaStar}
                  size={isPhone ? "34px" : "46px"}
                  sidewaysSize="30px"
                  color="#f6c343"
                  outline="#b45309"
                  left="-18px"
                  top="-20px"
                  transform="rotate(-15deg)"
                />
              }
              onClick={() => handleStartPlaying("regular")}
              w="100%"
              maxW="250px"
              sx={{ [SIDEWAYS_MQ]: SIDEWAYS_GAME_BUTTON }}
            >
              Regular Game
            </PillButton>
            <PillButton
              scheme="purple"
              subtitle="For older players"
              uppercase
              sticker={
                <Deco
                  icon={FaRocket}
                  size={isPhone ? "32px" : "44px"}
                  sidewaysSize="30px"
                  color="#8b5cf6"
                  outline="white"
                  right="-18px"
                  top="-24px"
                  transform="rotate(-10deg)"
                />
              }
              onClick={() => handleStartPlaying("srh")}
              w="100%"
              maxW="236px"
              sx={{ [SIDEWAYS_MQ]: SIDEWAYS_GAME_BUTTON }}
            >
              6th Grade &amp; Up
            </PillButton>
          </Stack>
        </VStack>
      </IllustratedCard>
    </IllustratedScreen>
  )
}

export default StartScreen
